
import { useRef } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { KanbanCard } from '@/components/kanban/KanbanCard';
import { CreateCardButton } from '@/components/kanban/CreateCardButton';
import { useMobileColumnTouch } from '@/hooks/useMobileColumnTouch';
import type { Tables } from '@/integrations/supabase/types';

type Column = Tables<'columns'>;
type Card = Tables<'cards'> & {
  assigned_profile?: Tables<'profiles'>;
};

interface MobileColumnViewProps {
  columns: Column[];
  currentColumnIndex: number;
  onColumnChange: (index: number) => void;
  cards: Card[];
  filteredCards: Card[];
  showArchivedCards: boolean;
}

export const MobileColumnView = ({
  columns,
  currentColumnIndex,
  onColumnChange,
  cards,
  filteredCards,
  showArchivedCards
}: MobileColumnViewProps) => {
  const currentColumn = columns[currentColumnIndex];

  // Use centralized touch handler for column swiping
  const { containerRef } = useMobileColumnTouch({
    onColumnSwipe: (direction) => {
      if (direction === 'right' && currentColumnIndex > 0) {
        onColumnChange(currentColumnIndex - 1);
      } else if (direction === 'left' && currentColumnIndex < columns.length - 1) {
        onColumnChange(currentColumnIndex + 1);
      }
    },
    currentColumnIndex,
    totalColumns: columns.length
  });

  if (!currentColumn) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">No columns available</p>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="h-full flex flex-col mobile-column-container"
    >
      {/* Edge swipe zones - CSS controlled touch behavior */}
      <div className="swipe-edge-left" />
      <div className="swipe-edge-right" />
      
      <div className="flex-1">
        <Droppable droppableId={currentColumn.id}>
          {(provided, snapshot) => (
            <div 
              ref={provided.innerRef} 
              {...provided.droppableProps} 
              className={`h-full mobile-column-content p-4 space-y-3 ${
                snapshot.isDraggingOver ? 'bg-blue-50 dark:bg-blue-950' : ''
              }`}
              data-column-id={currentColumn.id}
            >
              {cards.map((card, index) => (
                <Draggable key={card.id} draggableId={card.id} index={index}>
                  {(provided, snapshot) => (
                    <div 
                      ref={provided.innerRef} 
                      {...provided.draggableProps} 
                      {...provided.dragHandleProps} 
                      className={snapshot.isDragging ? 'rotate-1 shadow-lg scale-105' : ''}
                    >
                      <KanbanCard card={card} />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              
              <div className="pt-4">
                <CreateCardButton columnId={currentColumn.id} />
              </div>
            </div>
          )}
        </Droppable>
      </div>

      {/* Swipe hint for first-time users */}
      {columns.length > 1 && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded backdrop-blur-sm">
          Swipe edges to navigate columns
        </div>
      )}
    </div>
  );
};
